import React, { useRef, useState } from 'react'
import { makeStyles, ConfirmationDialog, Menu, MenuItem } from '@paintscout/ui'
import type { MuiTheme as Theme, StyleClasses } from '@paintscout/ui'
import { Editor, useDialogs, useUser, useClientOptions } from '@paintscout/ui-business'
import DashboardActivity from 'src/components/pages/dashboard/Dashboard/DashboardActivity'
import { EditIntegrationItemFilesDialog, EditItemFilesDialog } from '@paintscout/react-quote'
import type { QuoteFile } from 'paintscout'
import type { NoteActivity } from '@paintscout/api'
import { useFlags } from 'shared/components/common/FlagsProvider'
import { hasIntegrationInterface } from '@paintscout/util/builder'
import MediaIcon from '@material-ui/icons/PermMedia'
import ActivityLink from '../ActivityLink'

const useStyles = makeStyles<Theme, NotesDocListProps>(
  (theme) => ({
    root: {},
    editorContainer: {
      padding: theme.spacing(2)
    }
  }),
  { name: 'NotesDocList' }
)

export interface NotesDocListProps {
  classes?: StyleClasses<typeof useStyles>
  query: string
  docId: string
  noResultsMessage?: string
  disabled?: boolean
  onSaveNote: (files: QuoteFile[], note: string) => Promise<void>
  onDeleteNote: (note: NoteActivity) => Promise<void>
  onEditNote: (note: NoteActivity) => void
  onNoteFileDetails: (fileId: string) => QuoteFile
  onEditNoteFiles: (note: NoteActivity) => void
}

function NotesDocList(props: NotesDocListProps) {
  const classes = useStyles(props)
  const {
    docId,
    query,
    noResultsMessage = 'No notes found.',
    onSaveNote,
    onDeleteNote,
    onEditNote,
    onNoteFileDetails,
    onEditNoteFiles,
    disabled = false
  } = props
  const currentNote = ''
  const notesRef = useRef(null)
  const [loading, setLoading] = useState(false)
  const { openDialog, dismissDialog } = useDialogs()
  const [files, setFiles] = useState<QuoteFile[]>([])
  const { crm: crmFlag } = useFlags()
  const { isCrew } = useUser()
  const { options } = useClientOptions()
  const hasCompanyCam = hasIntegrationInterface({ options, integration: 'companycam' })
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null)

  async function handleDrop(uploadedFiles: File[]) {
    setLoading(true)
    openDialog(EditItemFilesDialog, {
      fileDocId: docId,
      titleOnly: true,
      uploadFiles: uploadedFiles,
      files,
      index: files.length,
      onConfirm: async (newFiles, _deletedFiles) => {
        setFiles(newFiles)
        dismissDialog()
        setLoading(false)
      },
      onCancel: () => {
        dismissDialog()
        setLoading(false)
      },
      onDelete: (key) => {
        setFiles((prevFiles) => prevFiles.filter((f) => f.key !== key))
      }
    })
  }

  function handleAddFiles(e?: React.MouseEvent<HTMLDivElement>) {
    if (hasCompanyCam && crmFlag) {
      setMenuAnchorEl(e?.currentTarget ?? null)
    } else {
      setLoading(true)
      openDialog(EditItemFilesDialog, {
        fileDocId: docId,
        titleOnly: true,
        files,
        index: files.length,
        onDelete: (key) => {
          setFiles((prevFiles) => prevFiles.filter((f) => f.key !== key))
        },
        onConfirm: async (newFiles, _deletedFiles) => {
          console.log('setting files', { newFiles })
          setFiles(newFiles)
          dismissDialog()
          setLoading(false)
        },
        onCancel: () => {
          dismissDialog()
          setLoading(false)
        }
      })
    }
  }

  function handleMenuClose() {
    setMenuAnchorEl(null)
  }

  function handleDeviceClick() {
    handleMenuClose()
    setLoading(true)
    openDialog(EditItemFilesDialog, {
      fileDocId: docId,
      titleOnly: true,
      files,
      index: files.length,
      onDelete: (key) => {
        setFiles((prevFiles) => prevFiles.filter((f) => f.key !== key))
      },
      onConfirm: async (newFiles, _deletedFiles) => {
        console.log('setting files', { newFiles })
        setFiles(newFiles)
        dismissDialog()
        setLoading(false)
      },
      onCancel: () => {
        dismissDialog()
        setLoading(false)
      }
    })
  }

  function handleCompanyCamClick() {
    handleMenuClose()
    setLoading(true)
    openDialog(EditIntegrationItemFilesDialog, {
      provider: 'companycam',
      maxWidth: 'lg',
      fullWidth: true,
      files,
      onConfirm: (newFiles: QuoteFile[]) => {
        const now = Date.now()
        const existingKeys = new Set(files.map((f) => f.key))
        const timestampedNewFiles = newFiles
          .filter((f) => !existingKeys.has(f.key))
          .map((f) => ({ ...f, created: f.created ?? now }))
        const mergedFiles = [...files, ...timestampedNewFiles]
        setFiles(mergedFiles)
        dismissDialog()
        setLoading(false)
      },
      onCancel: () => {
        dismissDialog()
        setLoading(false)
      }
    })
  }

  async function handleSave() {
    await onSaveNote(files, notesRef.current?.getHTML())
    setFiles([])
    notesRef.current?.clearContent()
    notesRef.current?.setHTML('')
  }

  function handleNoteClear() {
    openDialog(ConfirmationDialog, {
      title: 'Clear note',
      message: 'Are you sure you want to clear the note? All files and details will be lost, this cannot be undone!',
      scary: true,
      onConfirm: () => {
        notesRef.current?.clearContent()
        notesRef.current?.setHTML('')
        setFiles([])
        dismissDialog()
      },
      onCancel: dismissDialog
    })
  }

  function handleRemoveFile(file: QuoteFile) {
    setFiles((prevFiles) => prevFiles.filter((f) => f.key !== file.key))
  }

  function handleEditFile(file: QuoteFile) {
    openDialog(EditItemFilesDialog, {
      fileDocId: docId,
      files: files,
      index: files.findIndex((f) => f.key === file.key),
      titleOnly: true,
      onDelete: (key) => {
        setFiles((prevFiles) => prevFiles.filter((f) => f.key !== key))
      },
      onConfirm: (newFiles, _deletedFiles) => {
        setFiles(newFiles)
        dismissDialog()
      },
      onCancel: dismissDialog
    })
  }

  function handleNoteAction(action: string, activity: NoteActivity) {
    if (action === 'edit') {
      onEditNote(activity)
    } else if (action === 'edit-file') {
      onEditNoteFiles(activity)
    } else if (action === 'delete') {
      onDeleteNote(activity)
    } else if (action.includes('file')) {
      return onNoteFileDetails(action)
    }
  }

  return (
    <div className={classes.root}>
      {crmFlag && !isCrew && (
        <div className={classes.editorContainer}>
          <Editor
            content={currentNote}
            ref={notesRef}
            fullWidth={true}
            onAddFiles={handleAddFiles}
            onDrop={handleDrop}
            onEditFile={handleEditFile}
            disabled={loading || disabled}
            onSave={handleSave}
            files={files}
            onClear={handleNoteClear}
            onRemoveFile={handleRemoveFile}
            showFileBar
          />
          {hasCompanyCam && (
            <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={handleMenuClose}>
              <MenuItem icon={MediaIcon} onClick={handleDeviceClick}>
                My Device
              </MenuItem>
              <MenuItem
                image="https://res.cloudinary.com/taptapquote/image/upload/v1641926710/paintscout/providers/companycam.png"
                onClick={handleCompanyCamClick}
              >
                CompanyCam
              </MenuItem>
            </Menu>
          )}
        </div>
      )}
      <div>
        <DashboardActivity
          skipSanitize={!isCrew}
          query={query}
          actions
          disabled={disabled || loading}
          ActivityComponent={ActivityLink}
          noResultsMessage={noResultsMessage}
          showContent
          onAction={handleNoteAction}
        />
      </div>
    </div>
  )
}

export default NotesDocList
